import moment from 'moment'
import DetailData from 'pages/contacts/components/DetailData'
import ical from 'ical.js'
import { parseRFC822Email } from 'pages/main/components/EmailSelect/nodeHelper'

interface Person {
  last_name?: string
  first_name?: string
  middle_name?: string
  prefix?: string
  suffix?: string
  full_name?: string
  display_name?: string
  nickname?: string
  phonetic_first_name?: string
  phonetic_last_name?: string
  phonetic_middle_name?: string
}

interface Organization {
  job_title?: string
  company?: string | null
  department?: string | null
  phonetic_company?: string
}

interface Phone {
  type: string | null
  number: string
}

interface Email {
  type: string | null
  email: string
}

interface Address {
  type: string
  po_box?: string
  extended_address?: string
  street?: string
  city?: string
  state?: string
  post_code?: string
  country?: string
}

interface Link {
  type: string | null
  url: string
}

interface Impp {
  type: string
  value: string
}

interface DateItem {
  type: string
  date: string
}

interface Note {
  content: string
}

interface RelatedPerson {
  type: string
  name: string
}

interface CustomField {
  type: string | null
  value: any
  attributes: any
}

interface VCardResult {
  isPersonal: boolean
  person: Person
  organization: Organization
  photo: string | null
  hasPhoto?: boolean
  uid?: string
  version?: string
  prodid?: string
  rev?: string
  phones?: Phone[]
  emails?: Email[]
  addresses?: Address[]
  links?: Link[]
  impp?: Impp[]
  dates?: DateItem[]
  notes?: Note[]
  relatedPersons?: RelatedPerson[]
  customFields?: CustomField[]
}

interface AttributeItem {
  value: any
  attributes: Record<string, any>
}

interface ConvertedContact {
  result: VCardResult
  allValues: string
  emails?: string
  phones?: string
}

type VCardArray = [string, any, string, any]

export const convertVCardToContact = (vCard: any): ConvertedContact => {
  const listKeys = [
    'tel',
    'email',
    'adr',
    'url',
    'impp',
    'x-abdate',
    'x-ablabel',
    'x-abadr',
    'x-abrelatednames',
  ]

  const lists: Record<string, AttributeItem[]> = listKeys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: [],
    }),
    {},
  )

  const dates: DateItem[] = []
  const unknowns: any[] = []
  const result: VCardResult = { isPersonal: true, person: {}, organization: {}, photo: null }
  const notClassifys = ['version', 'uid', 'prodid', 'rev']
  const allValues: string[] = []

  if (vCard[0] === 'vcard') {
    vCard = vCard[1]
  }

  const names: string[] = []

  vCard.forEach((item: VCardArray) => {
    const key = item[0]
    const attributes = item[1]
    // const valueType = item[2]
    const value = item[3]

    if (notClassifys.includes(key)) {
      ;(result as any)[key] = value
    } else if (key === 'n') {
      value.forEach((item: unknown, index: number) => {
        const normalizedItem = normalizeStructuredValue(item)
        if (index === 0) {
          result.person.last_name = normalizedItem
        } else if (index === 1) {
          result.person.first_name = normalizedItem
        } else if (index === 2) {
          result.person.middle_name = normalizedItem
        } else if (index === 3) {
          result.person.prefix = normalizedItem
        } else if (index === 4) {
          result.person.suffix = normalizedItem
        }
        allValues.push(normalizedItem)
        names.push(normalizedItem.length > 0 ? normalizedItem : '')
      })
      const person: any = {
        firstName: result.person.first_name,
        lastName: result.person.last_name,
        middleName: result.person.middle_name,
        prefix: result.person.prefix,
        suffix: result.person.suffix,
      }
      result.person.full_name = DetailData.generateFullName(person as any)
    } else if (key === 'fn') {
      result.person.display_name = value
      allValues.push(value)
    } else if (key === 'nickname') {
      result.person.nickname = value
      allValues.push(value)
    } else if (key === 'x-phonetic-first-name') {
      result.person.phonetic_first_name = value
      allValues.push(value)
      names.push(value.length > 0 ? value : '')
    } else if (key === 'x-phonetic-last-name') {
      result.person.phonetic_last_name = value
      allValues.push(value)
      names.push(value.length > 0 ? value : '')
    } else if (key === 'x-phonetic-middle-name') {
      result.person.phonetic_middle_name = value
      allValues.push(value)
      names.push(value.length > 0 ? value : '')
    } else if (key === 'X-PHONETIC-ORG') {
      result.organization.phonetic_company = value
      allValues.push(value)
    } else if (key === 'title') {
      result.organization.job_title = value
      allValues.push(value)
    } else if (key === 'org') {
      result.organization.company = value[0] || null
      result.organization.department = value[1] || null
      allValues.push(value[0])
      allValues.push(value[1])
    } else if (key === 'bday') {
      dates.push({ type: 'birthday', date: moment(value).format('YYYY-MM-DD') || value })
      allValues.push(value)
    } else if (key === 'anniversary') {
      dates.push({ type: 'anniversary', date: moment(value).format('YYYY-MM-DD') || value })
      allValues.push(value)
    } else if (key === 'note') {
      if (value.trim().length > 0) {
        result.notes = [{ content: value }]
      }
      allValues.push(value)
    } else if (key === 'photo') {
      result.photo = value
    } else if (listKeys.includes(key)) {
      lists[key].push({
        value,
        attributes,
      })
      allValues.push(value)
    } else {
      unknowns.push(key)
    }
  })

  if (result.uid) {
    if (result.uid.startsWith('urn:uuid:')) {
      result.uid = result.uid.slice(9)
    }
  }

  const ablabels = lists['x-ablabel']
  _assignTel(result, lists['tel'], ablabels)
  _assignEmail(result, lists['email'], ablabels)
  _assignAddress(result, lists['adr'], ablabels)
  _assignLink(result, lists['url'], ablabels)
  _assignIMPP(result, lists['impp'], ablabels)
  result.dates = dates
  _assignDate(result, lists['x-abdate'], ablabels)
  _assignRelated(result, lists['x-abrelatednames'], ablabels)

  const customFields: CustomField[] = []
  unknowns.forEach((unknown) => {
    if (typeof unknown !== 'object') {
      return
    }

    const attributes = unknown.attributes
    const type = attributes?.['type'] || null
    // const group = attributes?.['group'] || null

    const value = unknown.value
    customFields.push({ type, value, attributes })
  })

  result.customFields = customFields

  const joinNames = names.join('\n').toLowerCase()

  let emails = result.emails?.reduce((acc, email) => {
    if (email == null || email.email == null) {
      return acc
    }
    return acc + email.email.toLowerCase() + '\n'
  }, '')
  emails += joinNames || ''

  const phones = result.phones?.reduce((acc, phone) => {
    if (phone.number == null) {
      return acc
    }
    return acc + phone.number + '\n'
  }, '')

  const retObj: ConvertedContact = {
    result,
    allValues: (allValues.join('\n') + '\n').toLowerCase(),
    emails,
    phones,
  }
  return retObj
}

const _assignTel = (
  result: VCardResult,
  tels: AttributeItem[],
  ablabels: AttributeItem[],
): void => {
  if (!tels || tels.length === 0) return

  const phones: Phone[] = []

  tels.forEach((tel) => {
    const attributes = tel.attributes

    let type: string | string[] | null = null
    type = attributes['type']
    if (Array.isArray(type)) {
      const filtered = type.filter(
        (item) => item.toLowerCase() !== 'pref' && item.toLowerCase() !== 'internet',
      )
      if (filtered.length > 0) {
        type = filtered[0].toLowerCase()
      } else {
        type = ''
      }
    }

    if (type === 'home') {
      type = 'home'
    } else if (type === 'work') {
      type = 'work'
    } else if (type === 'mobile' || type === 'cell') {
      type = 'mobile'
    } else if (type === 'fax') {
      type = 'fax'
    } else if (type === 'pager') {
      type = 'pager'
    } else if (type === 'other') {
      type = 'other'
    } else {
      let group = attributes['group']
      if (group) {
        const label = ablabels.find((item) => item.attributes.group === group)
        if (label) {
          type = String(label.value)
        }
      }
    }

    phones.push({ type: type as string | null, number: tel.value })
  })

  result.phones = phones
}

const _assignEmail = (
  result: VCardResult,
  emails: AttributeItem[],
  ablabels: AttributeItem[],
): void => {
  if (!emails || emails.length === 0) return

  const emailAddresses: Email[] = []

  emails.forEach((email) => {
    const attributes = email.attributes
    let type: string | string[] | null = null
    type = attributes['type']
    if (Array.isArray(type)) {
      const filtered = type.filter(
        (item) => item.toLowerCase() !== 'pref' && item.toLowerCase() !== 'internet',
      )
      if (filtered.length > 0) {
        type = filtered[0].toLowerCase()
      } else {
        type = ''
      }
    } else {
      type = type ? type.toLowerCase() : null
    }

    if (type === 'home') {
      type = 'home'
    } else if (type === 'work') {
      type = 'work'
    } else if (type === 'other') {
      type = 'other'
    } else {
      let group = attributes['group']
      if (group) {
        const label = ablabels.find((item) => item.attributes.group === group)
        if (label) {
          type = String(label.value)
        }
      }
    }

    const parsed = parseRFC822Email(email.value)
    emailAddresses.push({ type: type as string | null, email: parsed.email })
  })

  result.emails = emailAddresses
}

const _assignAddress = (
  result: VCardResult,
  addresses: AttributeItem[],
  ablabels: AttributeItem[],
): void => {
  if (!addresses || addresses.length === 0) return

  const realAddresses: Address[] = []
  addresses.forEach((address) => {
    const attributes = address.attributes
    let type: string | string[] | null = null
    type = attributes['type']
    if (Array.isArray(type)) {
      const filtered = type.filter(
        (item) => item.toLowerCase() !== 'pref' && item.toLowerCase() !== 'internet',
      )
      if (filtered.length > 0) {
        type = filtered[0].toLowerCase()
      } else {
        type = ''
      }
    } else {
      type = type ? type.toLowerCase() : ''
    }

    if (type === 'home' || type === 'work' || type === 'other') {
    } else {
      const label = ablabels.find((item) => item.attributes.group === attributes.group)
      if (label) {
        type = String(label.value)
      } else {
        type = ''
      }
    }

    const value = address.value
    const valueAddress: Partial<Address> = {}
    valueAddress.po_box = normalizeStructuredValue(value[0])
    valueAddress.extended_address = normalizeStructuredValue(value[1])
    valueAddress.street = normalizeStructuredValue(value[2])
    valueAddress.city = normalizeStructuredValue(value[3])
    valueAddress.state = normalizeStructuredValue(value[4])
    valueAddress.post_code = normalizeStructuredValue(value[5])
    valueAddress.country = normalizeStructuredValue(value[6])

    realAddresses.push({ type: type as string, ...valueAddress } as Address)
  })

  result.addresses = realAddresses
}

const _assignLink = (
  result: VCardResult,
  links: AttributeItem[],
  ablabels: AttributeItem[],
): void => {
  if (!links || links.length === 0) return

  const realLinks: Link[] = []

  links.forEach((link) => {
    const attributes = link.attributes
    let type: string | string[] | null | undefined = null
    type = attributes['type']
    if (Array.isArray(type)) {
      const filtered = type.filter(
        (item) => item.toLowerCase() !== 'pref' && item.toLowerCase() !== 'internet',
      )
      if (filtered.length > 0) {
        type = filtered[0].toLowerCase()
      } else {
        type = ''
      }
    } else {
      type = type?.toLowerCase()
    }

    if (
      type === 'facebook' ||
      type === 'linkedIn' ||
      type === 'github' ||
      type === 'skype' ||
      type === 'twitter' ||
      type === 'whatsapp' ||
      type === 'instagram' ||
      type === 'other' ||
      type === 'website'
    ) {
    } else {
      let group = attributes['group']
      if (group) {
        const label = ablabels.find((item) => item.attributes.group === group)
        if (label) {
          type = String(label.value)
        }
      }
    }

    if (type === '_$!<HomePage>!$_') {
      type = 'HomePage'
    }

    realLinks.push({ type: type as string | null, url: link.value })
  })

  result.links = realLinks
}

const _assignIMPP = (
  result: VCardResult,
  impps: AttributeItem[],
  ablabels: AttributeItem[],
): void => {
  if (!impps || impps.length === 0) return

  const realImpps: Impp[] = []

  impps.forEach((impp) => {
    const attributes = impp.attributes
    let type: string | string[] | null | undefined = null
    type = attributes['type']
    if (Array.isArray(type)) {
      const filtered = type.filter((item) => item.toLowerCase() !== 'pref')
      if (filtered.length > 0) {
        type = filtered[0].toLowerCase()
      } else {
        type = ''
      }
    } else {
      type = type?.toLowerCase()
    }

    if (type && typeof type === 'string' && type.length > 0) {
    } else {
      type = 'custom'
      let group = attributes['group']
      if (group) {
        const label = ablabels.find((item) => item.attributes.group === group)
        if (label) {
          type = String(label.value)
        }
      }
    }

    realImpps.push({ type: (type as string) || 'custom', value: impp.value })
  })

  result.impp = realImpps
}

const _assignDate = (
  result: VCardResult,
  abdates: AttributeItem[],
  ablabels: AttributeItem[],
): void => {
  if (!abdates || abdates.length === 0) return

  const realDates: DateItem[] = []

  abdates.forEach((date) => {
    const attributes = date.attributes
    let value = date.value

    const valueDate = new Date(value)
    if (!isNaN(valueDate.getTime())) {
      value = moment(valueDate).format('YYYY-MM-DD')
    }

    let type = ''
    let group = attributes['group']
    if (group) {
      const label = ablabels.find((item) => item.attributes.group === group)
      if (label && label.value) {
        type = label.value
        type = type.replace('_$!<', '').replace('>!$_', '')
        type = type.toLowerCase()
      }
    }

    realDates.push({ type, date: date.value })
  })

  result.dates!.push(...realDates)
}

const _assignRelated = (
  result: VCardResult,
  related: AttributeItem[],
  ablabels: AttributeItem[],
): void => {
  if (!related || related.length === 0) return

  const realRelated: RelatedPerson[] = []

  related.forEach((related) => {
    const attributes = related.attributes
    let type: string | null = null
    const group = attributes['group']

    if (group) {
      const label = ablabels.find((item) => item.attributes.group === group)
      if (label) {
        type = label.value
        type = type.replace('_$!<', '').replace('>!$_', '')
        type = type.toLowerCase()
      }
    }
    if (type === null) {
      type = ''
    }

    // related는 정식 tag가 아니어서 인지 decoding 되지 않고 전달되어 decoding 처리 추가.
    const value = decodeMIMEValue(related.value)
    realRelated.push({ type, name: value })
  })

  result.relatedPersons = realRelated
}

/**
 * vCard 구조화 값(ADR, N 등)의 컴포넌트 하나를 문자열로 정규화한다.
 *
 * ical.js 는 vCard 규격(RFC 6350)에 따라 구조화 값의 컴포넌트 내부에 이스케이프되지
 * 않은 `,` 가 있으면 해당 컴포넌트를 다중값으로 간주해 배열로 반환한다. 예를 들어
 * ADR 의 po_box 컴포넌트 원본이 `3524 Gutierrez Trail\nGrantport, VA 23766` 이면
 * 파서가 `,` 기준으로 잘라 `['3524 Gutierrez Trail\nGrantport', ' VA 23766']` 배열을
 * 돌려준다. vCard 규격상 컴포넌트 내 `,` 는 다중값 구분자이므로, 배열을 다시 `,` 로
 * join 하면 원본 문자열이 정확히 복원된다. 중첩 배열이 올 수도 있어 재귀적으로
 * 정규화하며, null/undefined 는 빈 문자열로, 그 외 값은 문자열로 변환한다.
 */
const normalizeStructuredValue = (value: unknown): string => {
  if (value == null) {
    return ''
  }
  if (Array.isArray(value)) {
    return value.map((item) => normalizeStructuredValue(item)).join(',')
  }
  return String(value)
}

const decodeMIMEValue = (value: string): string => {
  if (value.indexOf('\\') === -1) {
    return value
  }
  return value.replace(/\\n/g, '\n').replace(/\\r/g, '\r').replace(/\\;/g, ';')
}

export const parseVCards = (vCard: string): ConvertedContact[] => {
  let vCardData = ical.parse(vCard)
  if (vCardData[0] === 'vcard') {
    vCardData = [vCardData]
  }
  const vCards: ConvertedContact[] = []
  vCardData.forEach((vCard: any) => {
    const v = convertVCardToContact(vCard)
    if (v) {
      vCards.push(v)
    }
  })
  return vCards
}
